import React, { useEffect, useRef, useState } from "react";

import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import Divider from "@mui/material/Divider";

import { ReduxTextField } from "../../../../components/Redux/TextField";
import SettingSection from "../../../../components/SettingSection";
import TabSection from "../../../../components/styled/TabSection";
import {
  getRouteFormProperty,
  setIntegerRouteFormProperty,
  setRouteFormError,
  setRouteFormProperties,
  setRouteFormProperty,
} from "../../../../slices/routes";
import { useDispatch, useSelector } from "../../../../store";
import { validateInteger } from "../../../../validators/integer";
import { SectionProps } from "./common";

type MatchMode = "all" | "path" | "prefix" | "regex";
type RewriteMode = "none" | "prefix" | "regex";
export const Matchers = ({ readonly, tabValue }: SectionProps) => {
  const dispatch = useDispatch();
  const path = useSelector((state) => state.routes.form?.path || "");
  const prefix = useSelector((state) => state.routes.form?.prefix || "");
  const regex = useSelector((state) => state.routes.form?.regex || "");
  const prefixRewrite = useSelector(
    (state) => state.routes.form?.prefixRewrite || "",
  );
  const regexRewritePattern = useSelector(
    (state) => state.routes.form?.regexRewritePattern || "",
  );
  const [matchMode, setMatchMode] = useState<MatchMode>(
    regex ? "regex" : prefix ? "prefix" : path ? "path" : "all",
  );
  const [rewriteMode, setRewriteMode] = useState<RewriteMode>(
    regexRewritePattern ? "regex" : prefixRewrite ? "prefix" : "none",
  );
  const pathRef = useRef<HTMLInputElement>(null);
  const prefixRef = useRef<HTMLInputElement>(null);
  const regexRef = useRef<HTMLInputElement>(null);
  const prefixRewriteRef = useRef<HTMLInputElement>(null);
  const regexRewritePatternRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (matchMode === "path"
      ? pathRef
      : matchMode === "prefix"
        ? prefixRef
        : matchMode === "regex"
          ? regexRef
          : null
    )?.current?.focus();
  }, [matchMode]);
  useEffect(() => {
    (rewriteMode === "prefix"
      ? prefixRewriteRef
      : rewriteMode === "regex"
        ? regexRewritePatternRef
        : null
    )?.current?.focus();
  }, [rewriteMode]);

  const handleChangeMatchMode = (
    _: React.MouseEvent<HTMLElement, MouseEvent>,
    value: MatchMode,
  ) => {
    dispatch(
      setRouteFormProperties([
        { path: "path", value: "" },
        { path: "prefix", value: "" },
        { path: "regex", value: "" },
        { path: "regexPriorityOrder", value: "" },
      ]),
    );
    setMatchMode(value);
  };
  const handleChangeRewriteMode = (
    _: React.MouseEvent<HTMLElement, MouseEvent>,
    value: RewriteMode,
  ) => {
    dispatch(
      setRouteFormProperties([
        { path: "prefixRewrite", value: "" },
        { path: "regexRewritePattern", value: "" },
        { path: "regexRewriteSubstitution", value: "" },
      ]),
    );
    setRewriteMode(value);
  };

  return (
    <TabSection index={"matchers"} value={tabValue} title="Path Matching">
      <SettingSection label="Match Path">
        <ToggleButtonGroup
          aria-label="Path matching mode"
          value={matchMode}
          exclusive
          onChange={handleChangeMatchMode}
          size="medium"
          fullWidth
        >
          <ToggleButton value="all">All Paths</ToggleButton>
          <ToggleButton value="path">Exact Path</ToggleButton>
          <ToggleButton value="prefix">Prefix</ToggleButton>
          <ToggleButton value="regex">Regex</ToggleButton>
        </ToggleButtonGroup>
      </SettingSection>

      {matchMode === "path" && (
        <>
          <SettingSection
            label="Exact Path"
            docsKey="path"
            sx={{ pt: 0, pl: 3 }}
          >
            <ReduxTextField
              disabled={readonly}
              setter={setRouteFormProperty}
              getter={getRouteFormProperty}
              errorHandler={setRouteFormError}
              path="path"
              inputRef={pathRef}
            />
          </SettingSection>
        </>
      )}

      {matchMode === "prefix" && (
        <>
          <SettingSection label="Prefix" docsKey="prefix" sx={{ pt: 0, pl: 3 }}>
            <ReduxTextField
              disabled={readonly}
              setter={setRouteFormProperty}
              getter={getRouteFormProperty}
              errorHandler={setRouteFormError}
              path="prefix"
              inputRef={prefixRef}
            />
          </SettingSection>
        </>
      )}

      {matchMode === "regex" && (
        <>
          <SettingSection label="Regex" docsKey="regex" sx={{ pt: 0, pl: 3 }}>
            <ReduxTextField
              disabled={readonly}
              setter={setRouteFormProperty}
              getter={getRouteFormProperty}
              errorHandler={setRouteFormError}
              path="regex"
              inputRef={regexRef}
            />
          </SettingSection>

          <SettingSection
            label="Regex Priority Order"
            docsKey="regex-priority-order"
            sx={{ pt: 0, pl: 3 }}
          >
            <ReduxTextField
              disabled={readonly}
              setter={setIntegerRouteFormProperty}
              getter={getRouteFormProperty}
              errorHandler={setRouteFormError}
              path="regexPriorityOrder"
              validator={validateInteger}
            />
          </SettingSection>
        </>
      )}

      <Divider />

      <SettingSection label="Rewrite Path">
        <ToggleButtonGroup
          aria-label="Path rewrite mode"
          value={rewriteMode}
          exclusive
          onChange={handleChangeRewriteMode}
          size="medium"
          fullWidth
        >
          <ToggleButton value="none">Preserve Path</ToggleButton>
          <ToggleButton value="prefix">Prefix Rewrite</ToggleButton>
          <ToggleButton value="regex">Regex Rewrite</ToggleButton>
        </ToggleButtonGroup>
      </SettingSection>

      {rewriteMode === "prefix" && (
        <>
          <SettingSection
            label="Prefix Rewrite"
            docsKey="prefix-rewrite"
            sx={{ pt: 0, pl: 3 }}
          >
            <ReduxTextField
              disabled={readonly}
              path="prefixRewrite"
              setter={setRouteFormProperty}
              getter={getRouteFormProperty}
              errorHandler={setRouteFormError}
              inputRef={prefixRewriteRef}
            />
          </SettingSection>
        </>
      )}

      {rewriteMode === "regex" && (
        <>
          <SettingSection
            label="Regex Rewrite Pattern"
            docsKey="regex-rewrite"
            sx={{ pt: 0, pl: 3 }}
          >
            <ReduxTextField
              disabled={readonly}
              path="regexRewritePattern"
              getter={getRouteFormProperty}
              setter={setRouteFormProperty}
              errorHandler={setRouteFormError}
              inputRef={regexRewritePatternRef}
            />
          </SettingSection>

          <SettingSection
            label="Regex Rewrite Substitution"
            docsKey="regex-rewrite-substitution"
            sx={{ pt: 0, pl: 3 }}
          >
            <ReduxTextField
              disabled={readonly}
              path="regexRewriteSubstitution"
              getter={getRouteFormProperty}
              setter={setRouteFormProperty}
              errorHandler={setRouteFormError}
            />
          </SettingSection>
        </>
      )}
    </TabSection>
  );
};
